import React, { useEffect, useMemo, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import moment, { Moment } from "moment";
import { Col, Form, Modal, Row } from "antd";
import { OfferContext } from "./context/OfferContext";
import type {
  IOfferAggRuleFormData,
  IOfferContext,
  IOfferParamsFormData,
  IOfferTarifficationFormData,
  IOfferServiceProfileFormData,
} from "./context/types";
import { useAppDispatch, useAppSelector } from "../..";
import useDebounce from "../../lib/hooks/useDebounce";
import { hasAccess } from "../../components/RolesModule/hasAccess";
import type { Offer, ServiceRate } from "../../lib/types";
import { OfferList } from "../../components/OfferList/OfferList";
import { OfferParamsContainer } from "./OfferParamsContainer/OfferParamsContainer";
import { TarificationParamsContainer } from "./TarificationParamsContainer/TarificationParamsContainer";
import { ServiceProfileContainer } from "./ServiceProfileContainer/ServiceProfileContainer";
import { GroupOfferContainer } from "./GroupOfferContainer/GroupOfferContainer";
import { OfferCreateContainer } from "./OfferCreateContainer/OfferCreateContainer";
import { OfferPriceCreateContainer } from "./OfferPriceCreateContainer/OfferPriceCreateContainer";
import { OfferPriceViewContainer } from "./OfferPriceViewContainer/OfferPriceViewContainer";
import {
  fetchPaymentType,
  fetchProducts,
  fetchTariffModelTypes,
  fetchCurrenciesDic,
  fetchPriceUnits,
  fetchPeriodTypes,
  fetchTariffPeriodAlignments,
  fetchProfileTypeStatus,
  fetchInvoicePeriodStatus,
  fetchRateType,
  fetchChargeNames,
  fetchPrePaymentPeriodStatus,
  fetchTariffTypes,
  fetchRetailNetworks,
  fetchServiceUsageDocumentType,
  fetchServiceUsageDirection,
  fetchPriceEvents,
  fetchConditionTypes,
  fetchRateModes,
} from "../../store/slices/rtkDictionaries";
import {
  fetchOfferByIdFromVersions,
  fetchOfferServiceProfileById,
  fetchOfferVersionsNotForViewing,
  fetchServiceRatesFromVersions,
  fetchServiceTypeList,
  offeringActions,
} from "../../store/slices/offering/offering";
import { fetchCommonServiceProfiles } from "../../store/slices/service-profile";
import { toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import {
  fetchOfferingStatuses,
  fetchOfferingAccessTypes,
  fetchOfferingProlongTypes,
  fetchLifespanPeriods,
  fetchBillPeriods,
  fetchBundleTypes,
} from "../../store/slices/dicts";
import { fetchTax } from "../../store/slices/taxTs";
import { MENU_ADMIN_OFFERING_WRITE, OFFERING_PRICE_CHANGE } from "../../lib/const/permissions";
import { AccessForbidden } from "../../components/common";
import { hasLocalAccess } from "../../lib/configs";
import { useOfferList } from "../../components/OfferList/utils/useOfferList";
import { OFFER_STATUSES } from "../../store/slices/offering/const";
import { AggregationRulePage, AggregationRulesView } from "../../modules/AggregationRules";
import {
  aggregationRulesActions,
  fetchAggregationRulesFromVersions,
} from "../../modules/AggregationRules/store/slice/aggregationRulesSlice";
import { ymdDateFormat } from "../../lib/utils";
import { fetchServiceTypes } from "../../store/slices/services";

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferContainer = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { tenant } = useAppSelector((state) => state.tenants);
  const offer = (useAppSelector((state) => state.offering.offer) as unknown) as Offer | undefined;
  const { offerVersionsNotForViewing } = useAppSelector((state) => state.offering);
  const { products } = useAppSelector((state) => state.rtkDictionaries);

  const initialSearchValues = useMemo(() => {
    return {
      onDate: moment(),
      offeringStatusId: 2,
      page: 0,
      size: 10,
      // productId: products[0]?.id,
    };
  }, []);

  const [offerParamsForm] = Form.useForm<IOfferParamsFormData>();
  const [aggregationRulesForm] = Form.useForm<IOfferAggRuleFormData>();
  const [tarifficationForm] = Form.useForm<IOfferTarifficationFormData>();
  const [serviceProfileForm] = Form.useForm<IOfferServiceProfileFormData>();

  const offerListProps = useOfferList({
    initialOfferListViewMode: "view",
    initialSelectMode: "new",
    initialSearchValues,
  });

  // Загрузка справочников
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchOfferingStatuses());
    dispatch(fetchPaymentType());
    dispatch(fetchTariffModelTypes());
    dispatch(fetchCurrenciesDic());
    dispatch(fetchOfferingAccessTypes());
    dispatch(fetchOfferingProlongTypes());
    dispatch(fetchLifespanPeriods());
    dispatch(fetchPriceUnits());
    dispatch(fetchPeriodTypes());
    dispatch(fetchTariffPeriodAlignments());
    dispatch(fetchProfileTypeStatus());
    dispatch(fetchInvoicePeriodStatus());
    dispatch(fetchRateType());
    dispatch(fetchChargeNames());
    dispatch(fetchCommonServiceProfiles());
    dispatch(fetchPrePaymentPeriodStatus());
    dispatch(fetchBillPeriods());
    dispatch(fetchTariffTypes());
    dispatch(fetchRateModes());
    dispatch(
      fetchServiceTypeList({
        onDate: toHttpFormatStartOfDay(moment()),
        includeFutureDates: true,
        includePastDates: false,
      })
    );
    dispatch(fetchRetailNetworks());
    dispatch(fetchServiceUsageDocumentType());
    dispatch(fetchServiceUsageDirection());
    dispatch(fetchBundleTypes());
    dispatch(fetchPriceEvents());
    dispatch(fetchConditionTypes());
    dispatch(fetchServiceTypes());
  }, [dispatch]);

  const { offerId, offerOnDate } = useMemo(() => {
    const locationOfferId = Number(location.pathname?.split("/")?.[2]);
    const offerId = isNaN(locationOfferId) ? undefined : locationOfferId;
    const searchParams = new URLSearchParams(location.search);
    const offerOnDate = searchParams.get("onDate")?.replace(" ", "+");
    return { offerId, offerOnDate };
  }, [location]);

  // Обновление оффера (при неизменной дате), при необходимости
  const [offerForceUpdate, setOfferForceUpdate] = useState(false);

  const reloadOffer = () => {
    setOfferForceUpdate(!offerForceUpdate);
  };

  // Загрузка предложения по входу и очистка по выходу
  useEffect(() => {
    if (offerId && offerOnDate) {
      const onDate = toHttpFormatStartOfDay(moment(offerOnDate, ymdDateFormat));
      dispatch(
        fetchOfferByIdFromVersions({
          id: offerId,
          onDate,
          allObjectsVersions: true,
          withChars: true,
        })
      );
    } else {
      dispatch(offeringActions.cleanOffer());
    }
    return () => {
      dispatch(offeringActions.cleanOffer());
    };
  }, [offerId, offerOnDate, offerForceUpdate, dispatch]);

  // Создаваемый ruleCondition (для передачи в форму)
  const newRuleCondition: IOfferContext["newRuleCondition"] = {
    direction: "",
    relation: "OR",
  };

  // Создаваемый aggregationRule (для передачи в форму)
  const getNewAggregationRule: IOfferContext["getNewAggregationRule"] = () => ({
    id: Date.now(),
    ruleCondition: [newRuleCondition],
    // Эти танцы с бубном нужны, т.к. в форме глюк - остаются значения rangeFrom и rangeTo предыдущего ввода
    // @ts-ignore
    rangeFrom: "",
    // @ts-ignore
    rangeTo: "",
  });

  // Очистка данных профиля оплаты (нужно при переключении типа предложения: общий/индивидуальный)
  const clearServiceProfile = () => {
    serviceProfileForm.setFieldsValue({
      id: undefined,
      paymentTypeId: undefined,
      prepaymentToPayDays: undefined,
      invoicePeriodId: undefined,
      billToPayDays: undefined,
      prepaymentPeriodId: undefined,
      billPeriodId: undefined,
    });
  };

  // Обновление ставки налога в serviceRate, при измененении: fromDate, serviceTypeId
  const updateTaxRate = useDebounce((fromDate: Moment) => {
    const serviceTypeId =
      offerListProps.offerListSelectMode === "new"
        ? offerListProps.searchListValues.serviceTypeId
        : offerListProps.selectedOffer?.serviceTypeId;
    if (serviceTypeId) {
      dispatch(
        fetchTax({
          serviceTypeId,
          amount: 1, // Нужна только ставка налога, поэтому передается 1
          amountType: "NET",
          onDate: toHttpFormatStartOfDay(fromDate),
        })
      );
    }
  }, 300);

  // Значения которые serviceRate должен содержать по умолчанию (в данных формы не содержится)
  const requiredServiceRateValues: Partial<ServiceRate> = {
    tenantId: tenant.id,
    conditionTypeId: 16,
    rateRule: {
      ruleAccessTypeId: 10,
      ruleTypeId: 10,
    },
  };

  const [activeOfferVersion, setActiveOfferVersion] = useState<Offer>();

  // По дефолту activeOfferVersion === offer
  useEffect(() => {
    setActiveOfferVersion(offer);
  }, [offer]);

  // Загрузка вложенных сущностей оффера при смене версии
  useEffect(() => {
    // Проверка на наличие offerId нужна для случаев перехода из продукта в предложения. Т.к. не успевает очиститься предложение загруженное в продукте
    if (offerId && activeOfferVersion?.fromDate && activeOfferVersion.toDate) {
      const { fromDate, toDate } = activeOfferVersion;
      const aggRuleIds = activeOfferVersion.aggregationRules?.map(({ id }) => id);
      if (aggRuleIds?.length) {
        dispatch(
          fetchAggregationRulesFromVersions({
            ids: [...new Set(aggRuleIds)],
            fromDate,
            toDate,
          })
        );
      } else {
        dispatch(aggregationRulesActions.cleanAggregationRules());
      }
      const rateIds: number[] = [];
      const skipTsError = activeOfferVersion.aggregationRules?.forEach(({ serviceRates }) => {
        const skipTsError1 = serviceRates?.forEach(({ id }) => {
          if (id) {
            rateIds.push(id);
          }
        });
      });
      const skipTsError2 = activeOfferVersion.serviceRates?.forEach(({ id }) => {
        if (id) {
          rateIds.push(id);
        }
      });
      if (rateIds?.length) {
        dispatch(
          fetchServiceRatesFromVersions({
            ids: [...new Set(rateIds)],
            fromDate,
            toDate,
          })
        );
      } else {
        dispatch(offeringActions.cleanServiceRates());
      }
      const { serviceProfile } = activeOfferVersion;
      if (serviceProfile.id) {
        dispatch(
          fetchOfferServiceProfileById({
            id: serviceProfile.id,
            onDate: fromDate,
          })
        );
      } else {
        dispatch(offeringActions.cleanServiceProfile());
      }
    }
    return () => {
      dispatch(aggregationRulesActions.cleanAggregationRules());
      dispatch(offeringActions.cleanServiceRates());
      dispatch(offeringActions.cleanServiceProfile());
    };
  }, [activeOfferVersion, dispatch, offerId]);

  // По создании нужно при просмотре предложения выводить модалку с гиперссылками на разделы
  const [isFirstView, setIsFirstView] = useState(false);

  // Получение версий предложения. Нужны для определения доступности кнопки добавления цены
  useEffect(() => {
    // Проверка на наличие offerId (а не offer.id) нужна для случаев перехода из продукта в предложения. Т.к. не успевает очиститься предложение загруженное в продукте
    if (offerId) {
      dispatch(fetchOfferVersionsNotForViewing(offerId));
    } else {
      dispatch(offeringActions.cleanOfferVersionsNotForViewing());
    }
  }, [dispatch, offerId]);

  const priceCreationAllowed = useMemo(() => {
    const allowedByOfferVersionStatuses = offerVersionsNotForViewing.every(
      ({ offeringStatusId }) =>
        offeringStatusId === OFFER_STATUSES.PREPARE || offeringStatusId === OFFER_STATUSES.SUSPENDED
    );
    if (allowedByOfferVersionStatuses) {
      return true;
    }
    return hasAccess(OFFERING_PRICE_CHANGE);
  }, [offerVersionsNotForViewing]);

  const hasActiveOfferVersion = useMemo<boolean>(
    () =>
      offerVersionsNotForViewing.some(
        ({ offeringStatusId }) => offeringStatusId === OFFER_STATUSES.ACTIVE
      ),
    [offerVersionsNotForViewing]
  );

  const contextValue: IOfferContext = {
    activeOfferVersion,
    setActiveOfferVersion,
    offerParamsForm,
    tarifficationForm,
    aggregationRulesForm,
    serviceProfileForm,
    updateTaxRate,
    clearServiceProfile,
    requiredServiceRateValues,
    newRuleCondition,
    getNewAggregationRule,
    initialSearchValues,
    ...offerListProps,
    isFirstView,
    setIsFirstView,
    priceCreationAllowed,
    reloadOffer,
    hasActiveOfferVersion,
  };

  const firstViewModal = () => {
    if (!isFirstView) {
      return null;
    }
    const onAreaClick = () => {
      setIsFirstView(false);
    };
    return (
      <Modal visible={isFirstView} footer={null} title="Предложение создано" onCancel={onAreaClick}>
        <Row style={{ lineHeight: "32px" }}>
          <Col span={4} style={{ color: "lightgray" }}>
            Перейти в:
          </Col>
          <Col>
            <a href="#offering-params-title" aria-hidden="true" onClick={onAreaClick}>
              Предложение
            </a>
            <br />
            <a href="#offering-aggregationRules-title" aria-hidden="true" onClick={onAreaClick}>
              Группы трафика
            </a>
            <br />
            <a href="#offering-tariffication-title" aria-hidden="true" onClick={onAreaClick}>
              Тарификация
            </a>
            <br />
            <a href="#offering-profile-title" aria-hidden="true" onClick={onAreaClick}>
              Профиль оплаты
            </a>
            <br />
            {offer && offer?.bundleTypeId !== 1 && (
              <a href="#group-offering-title" aria-hidden="true" onClick={onAreaClick}>
                Групповое предложение
              </a>
            )}
          </Col>
        </Row>
      </Modal>
    );
  };

  return (
    <OfferContext.Provider value={contextValue}>
      <div className="container_height inner-row">
        <Switch>
          <Route exact path="/offer">
            <Redirect to="/offer/list" />
          </Route>
          <Route path="/offer/list">
            <OfferList filterName="main" {...offerListProps} displayPastVersion />
          </Route>
          <Route path="/offer/create">
            {hasAccess(MENU_ADMIN_OFFERING_WRITE) ? <OfferCreateContainer /> : <AccessForbidden />}
          </Route>
          <Route path="/offer/:offerId" exact>
            {firstViewModal()}
            <OfferParamsContainer />
            <AggregationRulesView
              activeOfferVersionId={activeOfferVersion?.id}
              priceCreationAllowed={priceCreationAllowed}
            />
            <TarificationParamsContainer />
            <ServiceProfileContainer />
            {hasLocalAccess("offering.groupOffer") && <GroupOfferContainer />}
          </Route>
          <Route path="/offer/:offerId/aggregation-rule/create" exact>
            <AggregationRulePage viewMode="create" />
          </Route>
          <Route path="/offer/:offerId/aggregation-rule/create/:ruleId" exact>
            <AggregationRulePage viewMode="createBasedOn" />
          </Route>
          <Route path="/offer/:offerId/aggregation-rule/:ruleId">
            <AggregationRulePage viewMode="view" />
          </Route>
          <Route path="/offer/:offerId/price/create" exact>
            <OfferPriceCreateContainer />
          </Route>
          <Route path="/offer/:offerId/price/create/:priceId" exact>
            <OfferPriceCreateContainer />
          </Route>
          <Route path="/offer/:offerId/price/:priceId">
            <OfferPriceViewContainer />
          </Route>
        </Switch>
      </div>
    </OfferContext.Provider>
  );
};
